import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Power } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORY_LABEL = { drug_coded: "Drug-coded", third_party: "Third-party", explicit: "Explicit" };
const KIND_LABEL = { word: "Word", phrase: "Phrase", emoji: "Emoji" };

export default function AdminTermsPage() {
  useSeo({ title: "Banned terms | LUX", ...PRIVATE_SEO });
  const client = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "terms"],
    queryFn: async () => (await api.get("/admin/terms")).data,
  });
  const [term, setTerm] = useState("");
  const [category, setCategory] = useState("third_party");
  const [kind, setKind] = useState("phrase");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const add = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/admin/terms", { term, category, kind });
      toast.success("Term added");
      setTerm("");
      await refetch();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id, active) => {
    try {
      await api.patch(`/admin/terms/${id}`, { active });
      await refetch();
      client.invalidateQueries({ queryKey: ["admin"] });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-terms-page">
      <header>
        <p className="adm-label">Settings</p>
        <h1 className="mt-2 text-3xl font-semibold text-admin-text">Banned terms</h1>
        <p className="mt-1 text-sm text-admin-muted">Words, phrases and emojis that score risk or auto-reject. Changes apply within a minute.</p>
      </header>

      <section className="adm-card p-5">
        <form onSubmit={add} className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr_auto] sm:items-end">
          <div>
            <label htmlFor="term" className="adm-label">Term</label>
            <input id="term" required maxLength={60} className="adm-input mt-1.5" value={term}
              onChange={(e) => setTerm(e.target.value)} data-testid="admin-terms-input" />
          </div>
          <div>
            <label className="adm-label">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="adm-input mt-1.5 h-[38px] justify-between" data-testid="admin-terms-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="adm-label">Kind</label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="adm-input mt-1.5 h-[38px] justify-between" data-testid="admin-terms-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(KIND_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button type="submit" disabled={busy} className="adm-btn adm-btn-primary" data-testid="admin-terms-add">
            <Plus className="h-4 w-4" /> Add
          </button>
        </form>
        {error && <p className="mt-2 text-sm text-[#B42318]" role="alert">{error}</p>}
      </section>

      <section className="adm-card overflow-hidden" data-testid="admin-terms-list">
        {isLoading ? <p className="p-5 text-sm text-admin-muted">Loading...</p> : (
          <ul className="divide-y divide-admin-border">
            {data?.terms?.map((t) => (
              <li key={t.id} className="flex items-center gap-3 px-4 py-3" data-testid="admin-terms-item">
                <code className="flex-1 font-mono text-sm text-admin-text">{t.term}</code>
                <span className="adm-pill border-admin-border bg-admin-surface2 text-admin-muted">{CATEGORY_LABEL[t.category] || t.category}</span>
                <span className="adm-pill border-admin-border bg-admin-surface2 text-admin-muted">{KIND_LABEL[t.kind] || t.kind}</span>
                <span className="text-xs text-admin-muted">{t.active ? "Active" : "Disabled"}</span>
                <button onClick={() => toggle(t.id, !t.active)} className="adm-btn adm-btn-outline" data-testid="admin-terms-toggle">
                  <Power className="h-3.5 w-3.5" /> {t.active ? "Disable" : "Enable"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
